import merge from 'lodash/merge';
import config from '../../config';
import RestEntityAPI from './restEntityAPI';
import logger from '../logger';
import fetch, { toQueryString } from '../fetch';

const options = {
  entityName: 'hiringEvents',
  endpoint: `${config.apiUrl}/v2/hiring-event`,
  searchEndpoint: `${config.apiUrl}/v2/hiring-event`,
};

const HiringEventAPI = () => {
  const parentAPI = RestEntityAPI(options);

  const searchHiringEvents = async (query) => {
    const { data } = await parentAPI.searchEntities(query);
    const { content, ...pagination } = data;
    return { data: content, ...pagination };
  };

  const register = async ({ eventId, register, profileKey }) => {
    const headers = (profileKey != null) ? { 'X-Profile-Key': profileKey } : {};

    const reqParams = [];
    reqParams.push(`${config.apiUrl}/v2/hiring-event/${eventId}/registrant/register`);
    reqParams.push('?register=');
    reqParams.push(`${register}`);

    try {
      return await fetch(reqParams.join(''), {
        method: 'POST',
        headers,
      });
    } catch (error) {
      logger.logDebug(`Failed to register for ${eventId}`, error);
      throw error;
    }
  };

  const unregister = async (eventId, profileKey) => {
    const headers = (profileKey != null) ? { 'X-Profile-Key': profileKey } : {};

    try {
      await fetch(`${config.apiUrl}/v2/hiring-event/${eventId}/registrant/unregister`, {
        method: 'POST',
        headers,
      });
    } catch (error) {
      logger.logDebug(`Failed to register for ${eventId}`, error);
      throw error;
    }
  };

  const getEvent = async ({ eventId, profileKey, ctx }) => {
    const getUrl = `${config.apiUrl}/v2/hiring-event/${eventId}`;
    const headers = (profileKey != null) ? {
      'X-Profile-Key': profileKey,
      'X-Check-Roles': true,
      'X-Rebrand-Flag': true,
    } : {};

    try {
      return await fetch(getUrl, {
        method: 'GET',
        headers,
        ctx: ctx || {},
      });
    } catch (error) {
      logger.logDebug(`Failed to get Hiring Event ${eventId}`, error);
      throw error;
    }
  };

  const getEvents = async (query, profileKey) => {
    const getUrl = `${config.apiUrl}/v2/hiring-event/search${toQueryString(query)}`;
    const headers = (profileKey != null) ? {
      'X-Profile-Key': profileKey,
      'X-Check-Roles': true,
      'X-Rebrand-Flag': true,
    } : {};

    try {
      return await fetch(getUrl, {
        method: 'GET',
        headers,
      });
    } catch (error) {
      logger.logDebug(`Failed to search Hiring Events for employer id ${query.employer_id}`, error);
      throw error;
    }
  };

  const getEmployerEventsStates = async (query, profileKey) => {
    const getUrl = `${config.apiUrl}/v2/hiring-event/states${toQueryString(query)}`;
    const headers = (profileKey != null) ? { 'X-Profile-Key': profileKey } : {};

    try {
      return await fetch(getUrl, {
        method: 'GET',
        headers,
      });
    } catch (error) {
      logger.logDebug(`Failed to fetch hiring events states for employer id ${query.employer_id}`, error);
      throw error;
    }
  };

  const createEvent = async (data) => {
    try {
      const response = await fetch(`${options.endpoint}`, {
        method: 'POST',
        body: data,
      });
      return response;
    } catch (error) {
      logger.logError('Encountered error while creating hiring event', error);
      throw error;
    }
  };

  const updateHiringEvent = async (query) => {
    const { eventId, data } = query;
    const body = { data: { key: eventId, ...data } };
    try {
      return await fetch(`${config.apiUrl}/v2/hiring-event`, {
        method: 'PATCH',
        body,
      });
    } catch (error) {
      logger.logDebug(`Failed to update for ${eventId}`, error);
      throw error;
    }
  };

  const getEventRegistrantsData = async (eventId, queryParams, requestBody) => {
    const url = `${config.apiUrl}/v1/hiring-event-registrant/${eventId}/registrants${toQueryString(queryParams)}`;

    try {
      const response = await fetch(url, {
        method: 'POST',
        body: {
          data: requestBody,
        },
        fullResponse: true,
      });
      return await response.json();
    } catch (error) {
      throw error;
    }
  };

  const getEventRegistrantsInCSV = async (eventId) => {
    const getUrl = `${config.apiUrl}/v1/hiring-event-registrant/${eventId}/registrants.csv`;

    try {
      const response = await fetch(getUrl, {
        method: 'GET',
        fullResponse: true,
      });
      return await response.text();
    } catch (error) {
      logger.logDebug(`Failed to get registrants for hiring event id ${eventId}`, error);
      throw error;
    }
  };

  const getRegistrantResume = async (registrantId, resumeFilename, resumeUri) => {
    const getUrl = `${config.apiUrl}/v1/hiring-event-registrant/${registrantId}/download-resume?disposition=inline`;
    const body = {
      data: {
        resume_filename: resumeFilename,
        resume_blob_uri: resumeUri,
      },
    };

    try {
      return await fetch(getUrl, {
        method: 'POST',
        body,
        fullResponse: true,
      });
    } catch (error) {
      logger.logDebug(`Failed to get resume for registrant id ${registrantId}`, error);
      throw error;
    }
  };

  const getRegistrantDetails = async (registrantId) => {
    const getUrl = `${config.apiUrl}/v1/hiring-event-registrant/${registrantId}`;

    try {
      const response = await fetch(getUrl, {
        method: 'GET',
        fullResponse: true,
      });
      return await response.json();
    } catch (error) {
      logger.logDebug(`Failed to get registrant details for registrant id ${registrantId}`, error);
      throw error;
    }
  };

  const deleteHiringEvent = async (eventId) => {
    try {
      await fetch(`${config.apiUrl}/v2/hiring-event/${eventId}`, {
        method: 'DELETE',
      });
      return;
    } catch (error) {
      logger.logDebug(`Failed to delete for ${eventId}`, error);
      throw error;
    }
  };

  const patchAttendanceStatus = async (registrantId, attendedEvent) => {
    try {
      await fetch(`${config.apiUrl}/v1/hiring-event-registrant/${registrantId}/attended/${attendedEvent}`, {
        method: 'PATCH',
        fullResponse: true,
      });
    } catch (error) {
      logger.logDebug(`Failed to patch attendence status for ${registrantId}`, error);
      throw error;
    }
  };

  return merge({}, { ...parentAPI },
    {
      searchHiringEvents,
      register,
      unregister,
      getEvent,
      getEvents,
      getEmployerEventsStates,
      getEventRegistrantsInCSV,
      getEventRegistrantsData,
      getRegistrantResume,
      getRegistrantDetails,
      createEvent,
      updateHiringEvent,
      deleteHiringEvent,
      patchAttendanceStatus,
    });
};

export default HiringEventAPI;
